import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthState, UserClaims } from "../types";
import { authService } from "../services/authService";

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
  refresh: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function buildInitialAuthState(): AuthState {
  const token = authService.getToken();
  if (!token) {
    return { loading: false, isAuthenticated: false, token: null, user: null };
  }
  const cached = authService.getCachedUserClaims(token);
  if (cached) {
    return { loading: false, isAuthenticated: true, token, user: cached };
  }
  return { loading: true, isAuthenticated: false, token, user: null };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<AuthState>(buildInitialAuthState);

  const load = async () => {
    let token = authService.getToken();
    if (!token) {
      setState({ loading: false, isAuthenticated: false, token: null, user: null });
      return;
    }

    let user = await authService.getUserInfo();
    if (authService.getToken() !== token) return;
    if (!user) {
      token = await authService.refreshToken(token);
      if (token) user = await authService.getUserInfo();
    }
    if (authService.getToken() !== token) return;
    if (!user) {
      authService.clearAuth();
      setState({ loading: false, isAuthenticated: false, token: null, user: null });
      return;
    }

    setState({
      loading: false,
      isAuthenticated: true,
      token,
      user
    });
  };

  useEffect(() => {
    // API retries also rotate tokens, independently of the context's refresh().
    const offRefreshed = authService.onRefreshed((token) => {
      setState((previous) => ({
        ...previous,
        token,
        user: authService.getCachedUserClaims(token) || previous.user
      }));
    });
    // AuthCallback stores the new tokens and immediately navigates away. A
    // verification request here can be aborted by that navigation and clear
    // those tokens. Verify on the destination page after the redirect instead.
    if (window.location.pathname === "/auth/callback" || window.location.pathname === "/auth/sso") {
      return offRefreshed;
    }
    void load();
    return offRefreshed;
  }, []);

  const login = useCallback(() => authService.redirectToLogin(), []);

  const logout = useCallback(() => {
    authService.clearAuth();
    setState({ loading: false, isAuthenticated: false, token: null, user: null });
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    const previousToken = authService.getToken();
    const token = await authService.refreshToken();
    if (!token) {
      if (authService.getToken() && authService.getToken() !== previousToken) return null;
      logout();
      return null;
    }
    const user: UserClaims | null = await authService.getUserInfo();
    if (authService.getToken() !== token) return null;
    if (!user) {
      logout();
      return null;
    }
    setState({
      loading: false,
      isAuthenticated: !!user,
      token,
      user
    });
    return token;
  }, [logout]);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      refresh
    }),
    [state, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
};
